import { MAX_HP } from '../constants.js';
import type { MatchConfig, WeaponId, WeaponTweaks } from '../types.js';

/**
 * Weapon balance knobs the menu can turn. Everything reads through these
 * helpers so clamping lives in one place: a hand-edited config (or a stale
 * client) can never smuggle a NaN or a ×9000 damage multiplier into the sim.
 */

/** Preset ladder the setup screen offers for starting health. */
export const HEALTH_PRESETS = [50, 100, 200, 300] as const;

/** Stock values the tuning screen shows when a knob is untuned. */
export const TWEAK_DEFAULTS = {
  chiliRounds: 2,
  lemonRounds: 3,
  beeLifeS: 6,
  beeStingDmg: 2,
  jumpPower: 1.5,
  startHealth: MAX_HP,
} as const;

/**
 * Magazine value meaning "no limit": passes every `ammo > 0` gate (999 shots
 * per match is de facto infinite) and the toolbar badges it as ∞. The tuning
 * stepper wraps 0 → ∞ → 20, so this is the only value above `ammoHi`.
 */
export const UNLIMITED_AMMO = 999;

/** Slider bounds the tuning screen enforces — mirrored by the clamps below. */
export const TWEAK_BOUNDS = {
  dmgLo: 0.25,
  dmgHi: 2,
  roundsLo: 0,
  roundsHi: 6,
  beeLifeLo: 1,
  beeLifeHi: 30,
  stingLo: 0,
  stingHi: 10,
  // Half-again the stock launch already roughly doubles jump height — beyond
  // that characters start clearing whole buildings, which breaks map design.
  jumpLo: 0.5,
  jumpHi: 1.5,
  // Tuned magazines: 0 (dry) to 20 (a whole crate), plus UNLIMITED_AMMO.
  ammoLo: 0,
  ammoHi: 20,
  // Soldier health presets live between "one good hit kills" and "a war of
  // attrition"; the set is fixed, but the clamp keeps a hand-edited config sane.
  healthLo: 25,
  healthHi: 300,
} as const;

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

const num = (v: unknown, lo: number, hi: number, def: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? clamp(v, lo, hi) : def;

/** Sanitize an untrusted tweaks object (server config, localStorage). */
export function clampTweaks(raw: unknown): WeaponTweaks | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const out: WeaponTweaks = {};

  if (r.dmg && typeof r.dmg === 'object') {
    const dmg: NonNullable<WeaponTweaks['dmg']> = {};
    for (const [k, v] of Object.entries(r.dmg as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v)) {
        dmg[k as WeaponId] = clamp(v, TWEAK_BOUNDS.dmgLo, TWEAK_BOUNDS.dmgHi);
      }
    }
    if (Object.keys(dmg).length) out.dmg = dmg;
  }
  if (r.ammo && typeof r.ammo === 'object') {
    const ammo: NonNullable<WeaponTweaks['ammo']> = {};
    for (const [k, v] of Object.entries(r.ammo as Record<string, unknown>)) {
      if (typeof v !== 'number' || !Number.isFinite(v)) continue;
      const n = Math.round(v);
      // Whole magazines only: the unlimited flag, or 0..ammoHi.
      if (n === UNLIMITED_AMMO) ammo[k as WeaponId] = UNLIMITED_AMMO;
      else ammo[k as WeaponId] = clamp(n, TWEAK_BOUNDS.ammoLo, TWEAK_BOUNDS.ammoHi);
    }
    if (Object.keys(ammo).length) out.ammo = ammo;
  }
  if (r.chiliRounds !== undefined)
    out.chiliRounds = Math.round(num(r.chiliRounds, TWEAK_BOUNDS.roundsLo, TWEAK_BOUNDS.roundsHi, TWEAK_DEFAULTS.chiliRounds));
  if (r.lemonRounds !== undefined)
    out.lemonRounds = Math.round(num(r.lemonRounds, TWEAK_BOUNDS.roundsLo, TWEAK_BOUNDS.roundsHi, TWEAK_DEFAULTS.lemonRounds));
  if (r.beeLifeS !== undefined)
    out.beeLifeS = num(r.beeLifeS, TWEAK_BOUNDS.beeLifeLo, TWEAK_BOUNDS.beeLifeHi, TWEAK_DEFAULTS.beeLifeS);
  if (r.beeStingDmg !== undefined)
    out.beeStingDmg = num(r.beeStingDmg, TWEAK_BOUNDS.stingLo, TWEAK_BOUNDS.stingHi, TWEAK_DEFAULTS.beeStingDmg);
  if (r.jumpPower !== undefined)
    out.jumpPower = num(r.jumpPower, TWEAK_BOUNDS.jumpLo, TWEAK_BOUNDS.jumpHi, TWEAK_DEFAULTS.jumpPower);
  if (r.startHealth !== undefined)
    out.startHealth = Math.round(
      num(r.startHealth, TWEAK_BOUNDS.healthLo, TWEAK_BOUNDS.healthHi, TWEAK_DEFAULTS.startHealth),
    );

  return Object.keys(out).length ? out : undefined;
}

/** Effective damage: base × the id's multiplier (1 when untuned). */
export function effDmg(cfg: MatchConfig | undefined, id: WeaponId, base: number): number {
  const m = cfg?.weaponTweaks?.dmg?.[id];
  return m != null && Number.isFinite(m)
    ? base * clamp(m, TWEAK_BOUNDS.dmgLo, TWEAK_BOUNDS.dmgHi)
    : base;
}

/**
 * Rounds a team starts with for `id`: the tuned magazine when one is set,
 * the weapon's `stock` count otherwise. UNLIMITED_AMMO rides through intact —
 * 999 > 0 passes every ammo gate until the last shot is spent (never).
 */
export function effAmmo(cfg: MatchConfig | undefined, id: WeaponId, stock: number): number {
  const n = cfg?.weaponTweaks?.ammo?.[id];
  if (typeof n !== 'number' || !Number.isFinite(n)) return stock;
  if (Math.round(n) === UNLIMITED_AMMO) return UNLIMITED_AMMO;
  return clamp(Math.round(n), TWEAK_BOUNDS.ammoLo, TWEAK_BOUNDS.ammoHi);
}

export function effChiliRounds(cfg: MatchConfig | undefined): number {
  return Math.round(num(cfg?.weaponTweaks?.chiliRounds, TWEAK_BOUNDS.roundsLo, TWEAK_BOUNDS.roundsHi, TWEAK_DEFAULTS.chiliRounds));
}

export function effLemonRounds(cfg: MatchConfig | undefined): number {
  return Math.round(num(cfg?.weaponTweaks?.lemonRounds, TWEAK_BOUNDS.roundsLo, TWEAK_BOUNDS.roundsHi, TWEAK_DEFAULTS.lemonRounds));
}

export function effBeeLifeS(cfg: MatchConfig | undefined): number {
  return num(cfg?.weaponTweaks?.beeLifeS, TWEAK_BOUNDS.beeLifeLo, TWEAK_BOUNDS.beeLifeHi, TWEAK_DEFAULTS.beeLifeS);
}

export function effBeeStingDmg(cfg: MatchConfig | undefined): number {
  return num(cfg?.weaponTweaks?.beeStingDmg, TWEAK_BOUNDS.stingLo, TWEAK_BOUNDS.stingHi, TWEAK_DEFAULTS.beeStingDmg);
}

/** Jump launch-speed multiplier — scaled into every solveJump maxSpeed. */
export function effJumpPower(cfg: MatchConfig | undefined): number {
  return num(cfg?.weaponTweaks?.jumpPower, TWEAK_BOUNDS.jumpLo, TWEAK_BOUNDS.jumpHi, TWEAK_DEFAULTS.jumpPower);
}

/** Starting/max HP every soldier spawns with — the "Squishy / Tanky" preset. */
export function effStartHealth(cfg: MatchConfig | undefined): number {
  return Math.round(
    num(cfg?.weaponTweaks?.startHealth, TWEAK_BOUNDS.healthLo, TWEAK_BOUNDS.healthHi, TWEAK_DEFAULTS.startHealth),
  );
}
